import { HttpError } from '@shared/interfaces/error.interface';
import { EntryModalComponent } from '@shared/modules/entry/components/entry-modal/entry-modal.component';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import {
  Component,
  Output,
  EventEmitter,
  Input,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  input,
  inject
} from '@angular/core';
import { GbxsoftInputTypes } from '@form/src/lib/gbxsoft-input/gbxsoft-input.types';
import { GbxsoftInputConfig } from '@form/src/lib/gbxsoft-input/interfaces/gbxsoft-input.interface';
import { TranslateService } from '@ngx-translate/core';
import { TextAttachmentsInputComponent } from '@shared/components/text-attachments-input/text-attachments-input.component';
import { ICommentaryEvent } from '../../interface/commentary-event.interface';
import { CommentaryEventType } from '../../enums/commentary-event-type.enum';
import { Commentary } from '../../models/commentary.model';
import { CommentaryService } from '../../services/commentary.service';
import { ICommentarySend } from '../../interface/commentary-send.interface';
import { CommentaryType } from '../../enums/commentary-type.enum';
import { Config } from '@shared/configs/config';
import { SnackBarService } from '@core/services/snackbar.service';
import * as moment from 'moment';
import { TextAttachmentsConfig } from '@shared/components/text-attachments-input/models/text-attachments-config';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { fadeIn, fadeOut } from '@shared/animations';
import {
  AttachmentLibraryDialogResult,
  AttachmentLibraryModalComponent
} from '@shared/modules/attachment-manage/components/attachment-library-modal/attachment-library-modal.component';
import { AttachmentsManageService } from '@shared/modules/attachment-manage/services/attachments-manage.service';
import { TextAttachment } from '@shared/components/text-attachments-input/models/text-attachment.model';
import { AttachmentPacket } from '@shared/modules/attachment-manage/models/attachment-packet.model';
import { Attachment } from '@shared/interfaces/attachment.interface';

const ATTACHMENT_FORM = {
  files: 'files',
  note: 'note'
};

export enum CommentaryViewType {
  DEFAULT,
  COMMENTS_FIRST
}

@Component({
  selector: 'commentary-manage-v2',
  templateUrl: './commentary-manage-v2.component.html',
  styleUrls: ['./commentary-manage-v2.component.scss'],
  providers: [CommentaryService, AttachmentsManageService],
  animations: [fadeIn, fadeOut],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommentaryManageV2Component {
  CommentaryViewType = CommentaryViewType;
  ATTACHMENT_FORM = ATTACHMENT_FORM;
  ButtonSize = ButtonSize;
  ButtonTypes = ButtonTypes;
  readonly projectId = input<number | null>(null);

  sortType: 'latest' | 'oldest' = 'latest';

  config: GbxsoftInputConfig = {
    name: ' ',
    type: GbxsoftInputTypes.TEXT,
    placeholder: this.t.instant('Commentaries.addCommentary'),
    showPlaceholderOnFocus: true
  };

  form: UntypedFormGroup = this.fb.group({
    [ATTACHMENT_FORM.files]: [null],
    [ATTACHMENT_FORM.note]: [null]
  });

  dialogRef: MatDialogRef<EntryModalComponent>;
  _commentaries: Commentary[] = [];
  loading: boolean = false;
  private sourceAttachmentIds: number[] = [];
  private readonly dialog = inject(MatDialog);

  @ViewChild('textAttach') textAttach: TextAttachmentsInputComponent;
  @ViewChild('commentaryInp', { static: false }) commentaryInp: ElementRef;
  @ViewChild('manageBox') manageBox: ElementRef;

  @Input()
  set commentaries(_commentaries) {
    this._commentaries = _commentaries?.length ? _commentaries : [];
    this.autoSort();
  }
  get commentaries() {
    return this._commentaries;
  }

  @Input() hideSort: boolean = false;
  @Input() showTitle: boolean = true;
  @Input() id: number = null;
  @Input() type: CommentaryType = CommentaryType.COMMENT;
  @Input() attachmentsConfig: TextAttachmentsConfig = {
    photoAttachments: true,
    fileAttachments: true,
    showText: true
  };
  @Output() commentaryEvent: EventEmitter<ICommentaryEvent> = new EventEmitter();

  get transformedForm(): ICommentarySend {
    const controls = this.form.controls;
    const content = controls[ATTACHMENT_FORM.note].value ? controls[ATTACHMENT_FORM.note].value : '';
    const attachments = (controls[ATTACHMENT_FORM.files].value || []).filter((attachment) => attachment instanceof File);
    const type = this.type;
    const id = this.id;

    return {
      id,
      content,
      attachments,
      attachmentPacketId: this.attachmentsManageService.attachmentPacket?.id || null,
      type
    };
  }

  get hasFormValues() {
    return (
      !!this.form.controls[ATTACHMENT_FORM.note].value?.length ||
      !!this.form.controls[ATTACHMENT_FORM.files].value?.length
    );
  }

  constructor(
    private t: TranslateService,
    private fb: UntypedFormBuilder,
    private service: CommentaryService,
    private s: SnackBarService,
    private changes: ChangeDetectorRef,
    private attachmentsManageService: AttachmentsManageService
  ) {}

  sortFromLatest() {
    this._commentaries.sort((a, b) => {
      return moment(b.created, Config.DATE_SERVER).unix() - moment(a.created, Config.DATE_SERVER).unix();
    });
    this.sortType = 'latest';
  }

  sortFromOldest() {
    this._commentaries.sort((a, b) => {
      return moment(a.created, Config.DATE_SERVER).unix() - moment(b.created, Config.DATE_SERVER).unix();
    });
    this.sortType = 'oldest';
  }

  resetForm() {
    this.form.reset();
    this.textAttach.removeAllAttachments();
    this.sourceAttachmentIds = [];
    this.attachmentsManageService.setInitialAttachmentPacket();
    this.changes.detectChanges();
  }

  openAttachmentLibrary(): void {
    const projectId = this.projectId();
    if (!projectId || this.loading || this.textAttach?.fileLoading) return;

    const maxAttachments = this.attachmentsConfig?.maxAttachments;
    const maxSelectable = maxAttachments ? Math.max(maxAttachments - this.textAttach.attachments.length, 0) : null;
    const dialogRef = this.dialog.open(AttachmentLibraryModalComponent, {
      width: '1120px',
      height: 'calc(100vh - 96px)',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '760px',
      autoFocus: false,
      disableClose: true,
      data: {
        projectId,
        attachmentsConfig: this.attachmentsConfig,
        attachedIds: this.sourceAttachmentIds,
        maxSelectable
      }
    });

    dialogRef.afterClosed().subscribe((result?: AttachmentLibraryDialogResult) => {
      if (result?.sourceAttachmentIds?.length) {
        this.addProjectLibraryFiles(projectId, result.sourceAttachmentIds);
      }
    });
  }

  removeLibraryAttachment(item: TextAttachment): void {
    if (!item.sourceAttachmentId) return;

    this.sourceAttachmentIds = this.sourceAttachmentIds.filter((id) => id !== item.sourceAttachmentId);
    const packetId = this.attachmentsManageService.attachmentPacket?.id;
    if (!packetId) return;

    this.attachmentsManageService.deleteAttachmentPacket(packetId).subscribe({
      next: () => {
        this.attachmentsManageService.setInitialAttachmentPacket();
        if (this.sourceAttachmentIds.length) {
          this.addProjectLibraryFiles(this.projectId(), this.sourceAttachmentIds, true);
        }
      },
      error: () => this.s.error(this.t.instant('Attachments.library.errorAdding'))
    });
  }

  private addProjectLibraryFiles(projectId: number, sourceAttachmentIds: number[], rebuild = false): void {
    const ids = sourceAttachmentIds.slice(0, 200);
    this.textAttach.fileLoading = true;
    this.changes.detectChanges();

    const packetId = this.attachmentsManageService.attachmentPacket?.id;
    const source$ = packetId
      ? this.attachmentsManageService.updateAttachmentPacketFromSources(packetId, projectId, ids)
      : this.attachmentsManageService.createAttachmentPacketFromSources(projectId, ids);

    source$
      .subscribe({
        next: (response) => {
          const packet = response.result;
          if (!packet) return;

          const addedIds = rebuild ? this.sourceAttachmentIds : ids.filter((id) => !this.sourceAttachmentIds.includes(id));
          this.sourceAttachmentIds = [...new Set([...this.sourceAttachmentIds, ...ids])];
          if (rebuild) {
            this.textAttach.attachments = this.textAttach.attachments.filter((item) => !item.sourceAttachmentId);
          }
          this.addLibraryAttachments(packet, addedIds);
          this.textAttach.setFilesToFormControl();
        },
        error: () => this.s.error(this.t.instant('Attachments.library.errorAdding'))
      })
      .add(() => {
        this.textAttach.fileLoading = false;
        this.changes.detectChanges();
      });
  }

  private addLibraryAttachments(packet: AttachmentPacket, sourceAttachmentIds: number[]): void {
    const attachments = (packet.attachments || []).slice(-sourceAttachmentIds.length);
    sourceAttachmentIds.forEach((sourceAttachmentId, index) => {
      const attachment = attachments[index] as Attachment;
      if (!attachment) return;
      this.textAttach.attachments.push(
        new TextAttachment({
          id: attachment.id?.toString(),
          name: attachment.name,
          file: attachment,
          isUploaded: true,
          sourceAttachmentId
        })
      );
    });
  }

  // #region ADD ACTION
  addCommentary() {
    if (this.loading || !this.hasFormValues) return;
    this.loading = true;
    this.changes.detectChanges();
    this.service
      .addCommentary(this.transformedForm)
      .subscribe({
        next: (c: Commentary) => this.successAddCommentary(c),
        error: (err: HttpError) => this.errorAddCommentary(err)
      })
      .add(() => {
        this.loading = false;
        this.changes.detectChanges();
      });
  }

  successAddCommentary(c: Commentary) {
    c = new Commentary(c);
    this.commentaries.push(c);
    this.resetForm();
    this.commentaryEvent.emit({ type: CommentaryEventType.ADD, data: { item: c, list: this.commentaries } });
    this.s.success(this.t.instant('Commentaries.commentaryAdded'));
    this.autoSort();
    this.manageBox?.nativeElement?.scrollIntoView({ behavior: 'smooth' });
    this.changes.detectChanges();
  }

  errorAddCommentary(err: HttpError) {
    this.s.error(this.t.instant('Commentaries.commentaryAddedFail'));
    this.changes.detectChanges();
  }
  //#endregion

  // #region DELETE ACTION
  deleteCommentary(c: Commentary) {
    this.service.deleteCommentary(c.id).subscribe({
      next: (_c: Commentary) => this.successDeleteCommentary(c),
      error: (err: HttpError) => this.errorDeleteCommentary(err)
    });
  }

  successDeleteCommentary(c: Commentary) {
    const index = this.commentaries.findIndex((i) => i.id === c.id);
    this.commentaries.splice(index, 1);
    this.commentaryEvent.emit({
      type: CommentaryEventType.REMOVE,
      data: { item: c, list: this.commentaries }
    });
    this.s.success(this.t.instant('Commentaries.commentaryRemoved'));
    this.autoSort();
    this.changes.detectChanges();
  }

  errorDeleteCommentary(err: HttpError) {
    this.s.error(this.t.instant('Commentaries.commentaryRemovedFail'));
    this.changes.detectChanges();
  }
  //#endregion

  editCommentary(event: any, commentary: Commentary) {
    const transformedForm = { type: this.type, id: this.id, ...event };
    this.changes.detectChanges();
    this.service.editCommentary(commentary.id, transformedForm).subscribe({
      next: (c: Commentary) => this.successEditCommentary(c),
      error: (err: HttpError) => this.errorEditCommentary(err)
    });
  }

  successEditCommentary(c: Commentary) {
    c = new Commentary(c);
    const index = this.commentaries.findIndex((i) => i.id === c.id);
    if (index > -1) {
      this.commentaries[index] = c;
      this.commentaryEvent.emit({
        type: CommentaryEventType.EDIT,
        data: { item: c, list: this.commentaries }
      });
    }
    this.s.success(this.t.instant('Commentaries.commentaryEdited'));
    this.dialogRef?.close();
    this.autoSort();
    this.changes.detectChanges();
  }

  errorEditCommentary(err: HttpError) {
    this.s.error(this.t.instant('Commentaries.commentaryEditedFail'));
    this.changes.detectChanges();
  }
  //#endregion

  private autoSort() {
    this.sortType === 'latest' ? this.sortFromLatest() : this.sortFromOldest();
  }

  emitCommentaryEvent($event: ICommentaryEvent) {
    switch ($event.type) {
      case CommentaryEventType.REMOVE:
        this.deleteCommentary($event.data);
        break;
      default:
        break;
    }
    this.changes.detectChanges();
  }

  trackCommentaries(index: number, commentary: Commentary) {
    return commentary.id;
  }
}
